import React, { useState, useEffect } from 'react';
import { X, Clock, Save, Calendar } from 'lucide-react';
import './DoctorScheduleModal.css';

import { API_URL } from '../config/api';

const DAYS_OF_WEEK = [
    { id: 0, name: 'Domingo', short: 'Dom' },
    { id: 1, name: 'Lunes', short: 'Lun' },
    { id: 2, name: 'Martes', short: 'Mar' },
    { id: 3, name: 'Miércoles', short: 'Mié' },
    { id: 4, name: 'Jueves', short: 'Jue' },
    { id: 5, name: 'Viernes', short: 'Vie' },
    { id: 6, name: 'Sábado', short: 'Sáb' }
];

const DoctorScheduleModal = ({ doctorId, doctorName, onClose, onSave }) => {
    const [schedules, setSchedules] = useState(
        DAYS_OF_WEEK.map(day => ({
            day_of_week: day.id,
            dayName: day.name,
            is_active: false,
            start_time: '09:00',
            end_time: '18:00'
        }))
    );
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchSchedule();
    }, [doctorId]);

    const fetchSchedule = async () => {
        if (!doctorId) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/doctor-schedules?doctor_id=${doctorId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();

                // Merge fetched data with default schedule
                if (Array.isArray(data) && data.length > 0) {
                    setSchedules(prev => prev.map(day => {
                        const found = data.find(d => d.day_of_week == day.day_of_week);
                        if (found) {
                            return {
                                ...day,
                                is_active: found.is_active == 1 || found.is_active === true,
                                start_time: found.start_time?.substring(0, 5) || '09:00',
                                end_time: found.end_time?.substring(0, 5) || '18:00'
                            };
                        }
                        return day;
                    }));
                }
            }
        } catch (err) {
            console.error('Error fetching schedule:', err);
            setError('Error al cargar horarios');
        } finally {
            setLoading(false);
        }
    };

    const handleDayToggle = (dayId) => {
        setSchedules(prev => prev.map(day =>
            day.day_of_week === dayId
                ? { ...day, is_active: !day.is_active }
                : day
        ));
    };

    const handleTimeChange = (dayId, field, value) => {
        setSchedules(prev => prev.map(day =>
            day.day_of_week === dayId
                ? { ...day, [field]: value }
                : day
        ));
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');

            // Only send active days
            const activeSchedules = schedules
                .filter(s => s.is_active)
                .map(s => ({
                    day_of_week: s.day_of_week,
                    start_time: s.start_time,
                    end_time: s.end_time,
                    is_active: 1
                }));

            const response = await fetch(`${API_URL}/doctor-schedules`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    doctor_id: doctorId,
                    schedules: activeSchedules
                })
            });

            if (response.ok) {
                alert('✅ Horarios guardados correctamente');
                if (onSave) onSave();
                onClose();
            } else {
                const data = await response.json();
                setError(data.message || 'Error al guardar');
            }
        } catch (err) {
            console.error('Error saving schedule:', err);
            setError('Error de conexión');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="schedule-modal-overlay" onClick={onClose}>
            <div className="schedule-modal" onClick={(e) => e.stopPropagation()}>
                <div className="schedule-modal-header">
                    <div className="header-title">
                        <Calendar size={24} />
                        <div>
                            <h2>Configurar Horarios</h2>
                            <p>Dr. {doctorName}</p>
                        </div>
                    </div>
                    <button className="btn-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="schedule-modal-body">
                    {loading ? (
                        <div className="loading-state">Cargando horarios...</div>
                    ) : (
                        <>
                            <p className="schedule-instruction">
                                Selecciona los días que trabaja el médico y define el horario de atención.
                            </p>

                            <div className="schedule-list">
                                {schedules.map(day => (
                                    <div
                                        key={day.day_of_week}
                                        className={`schedule-day ${day.is_active ? 'active' : ''}`}
                                    >
                                        <label className="day-toggle">
                                            <input
                                                type="checkbox"
                                                checked={day.is_active}
                                                onChange={() => handleDayToggle(day.day_of_week)}
                                            />
                                            <span className="day-name">{day.dayName}</span>
                                        </label>

                                        {day.is_active && (
                                            <div className="time-inputs">
                                                <div className="time-group">
                                                    <Clock size={14} />
                                                    <input
                                                        type="time"
                                                        value={day.start_time}
                                                        onChange={(e) => handleTimeChange(day.day_of_week, 'start_time', e.target.value)}
                                                        step="900"
                                                    />
                                                    <span>a</span>
                                                    <input
                                                        type="time"
                                                        value={day.end_time}
                                                        onChange={(e) => handleTimeChange(day.day_of_week, 'end_time', e.target.value)}
                                                        step="900"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {error && <div className="error-message">{error}</div>}
                        </>
                    )}
                </div>

                <div className="schedule-modal-footer">
                    <button className="btn-cancel" onClick={onClose}>
                        Cancelar
                    </button>
                    <button
                        className="btn-save"
                        onClick={handleSave}
                        disabled={saving || loading}
                    >
                        <Save size={16} />
                        {saving ? 'Guardando...' : 'Guardar Horarios'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DoctorScheduleModal;
